import { createSlice } from '@reduxjs/toolkit'
import { StoreData } from '../reducers/typings'
import {
  createTerritory,
  deleteTerritory,
  fetchTerritoryBoundaries,
  fetchTerritorySnapshot,
  fetchTerritorySummaries,
  mergeTerritories,
  splitTerritory,
  updateTerritory,
} from './api'
import { TerritoryState } from './typings'

/**
 * Records that a territory was just written, so a read that set off earlier cannot speak for it.
 *
 * The tick is global and monotonic rather than a timestamp: two writes inside the same
 * millisecond are ordinary, and Date.now() cannot separate them.
 */
function markWritten(state: TerritoryState, id: number): void {
  state.writeTick += 1
  state.authoritativeAt[id] = state.writeTick
}

export const initialState: TerritoryState = {
  summaries: [],
  summariesStatus: 'idle',
  boundariesById: {},
  loadingIds: [],
  writeTick: 0,
  authoritativeAt: {},
  readStartedAt: {},
  snapshotById: {},
  snapshotLoadingIds: [],
  snapshotErrorIds: [],
}

/**
 * Drop cached numbers for territories whose shape just changed or that no longer exist.
 *
 * Every boundary write goes through here. A redraw, split or merge moves records across the
 * line, so a snapshot taken before it describes an area that is no longer there -- and it would
 * be shown without any hint that it is stale, because the bubble cannot tell a cached answer
 * from a fresh one.
 */
function forgetSnapshots(state: TerritoryState, ids: number[]): void {
  const dropped = new Set(ids)

  for (const id of dropped) delete state.snapshotById[id]
  state.snapshotLoadingIds = state.snapshotLoadingIds.filter(
    (id) => !dropped.has(id),
  )
  state.snapshotErrorIds = state.snapshotErrorIds.filter(
    (id) => !dropped.has(id),
  )
}

/**
 * Whether a snapshot response is answering for a shape that has since been written over.
 *
 * Reads the tick recorded when the request set off and clears it, the same bookkeeping the
 * summaries and boundaries reads do -- kept here rather than inlined because the fulfilled and
 * rejected handlers must agree, and the entry has to be released on both paths or readStartedAt
 * grows for the life of the session.
 */
function snapshotSuperseded(
  state: TerritoryState,
  requestId: string,
  id: number,
): boolean {
  const startedAt = state.readStartedAt[requestId] ?? state.writeTick
  delete state.readStartedAt[requestId]

  return (state.authoritativeAt[id] ?? -1) > startedAt
}

/**
 * Replace a territory in the list by id, or append it when it is not there yet.
 *
 * A create or split response can arrive after a summaries fetch that the server built once the
 * write had already committed, so the territory is in both. Appending blind gave two entries
 * with the same id: duplicate React keys, and the polygon drawn twice.
 */
function upsertSummary(
  summaries: TerritoryState['summaries'],
  entry: TerritoryState['summaries'][number],
): TerritoryState['summaries'] {
  return summaries.some((t) => t.id === entry.id)
    ? summaries.map((t) => (t.id === entry.id ? entry : t))
    : [...summaries, entry]
}

/**
 * No reset reducer: createReducers already swaps the whole store back to initialStoreState on
 * logOut, and `territory` is registered there, so a slice-local clear would be a second path to
 * the same thing with nothing calling it.
 */
const territorySlice = createSlice({
  name: 'territory',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTerritorySummaries.pending, (state, action) => {
        state.readStartedAt[action.meta.requestId] = state.writeTick
        state.summariesStatus = 'loading'
      })
      .addCase(fetchTerritorySummaries.fulfilled, (state, action) => {
        const startedAt =
          state.readStartedAt[action.meta.requestId] ?? state.writeTick
        delete state.readStartedAt[action.meta.requestId]

        // Anything written AFTER this read set off is newer than the answer coming back, so the
        // answer must not speak for it. The case that bit: a summaries refresh issued moments
        // before createTerritory completed arrived afterwards, and -- since the new territory was
        // not in a payload the server built before it existed -- dropped the just-drawn polygon
        // from the list and evicted its geometry. It vanished off the map until a reload.
        const writtenSince = (id: number) =>
          (state.authoritativeAt[id] ?? -1) > startedAt

        const currentById = new Map(state.summaries.map((t) => [t.id, t]))

        // Payload entries, EXCEPT where a write landed after this read set off. Taking those
        // from the payload is how a stale response used to revive a territory the admin had
        // just deleted or merged away -- revived with no geometry, so useTerritoryOverlaps kept
        // it in pendingIds and the overlap warning stuck on 'incomplete' -- and how it undid a
        // rename or a colour change. The local entry wins; if the write was a delete there is
        // no local entry and the territory correctly stays gone.
        const reconciled = action.payload.flatMap((p) => {
          if (!writtenSince(p.id)) return [p]

          const current = currentById.get(p.id)
          return current ? [current] : []
        })

        // Written since the read AND absent from the payload: a territory created after the
        // server built this response, which would otherwise vanish off the map until a reload.
        const kept = state.summaries.filter(
          (t) =>
            writtenSince(t.id) && !action.payload.some((p) => p.id === t.id),
        )

        state.summaries = [...reconciled, ...kept]
        state.summariesStatus = 'loaded'

        // Drop cached geometry for territories that no longer exist, so a deleted territory
        // cannot keep drawing after a refresh -- but never for one this response is simply too
        // old to know about.
        // Built from what the slice now holds, not from the raw payload: an id the payload
        // still lists but a delete has since removed must not count as live, or its geometry
        // would survive the refresh that was supposed to clear it.
        const live = new Set([
          ...reconciled.map((t) => t.id),
          ...kept.map((t) => t.id),
        ])
        for (const id of Object.keys(state.boundariesById)) {
          if (!live.has(Number(id)) && !writtenSince(Number(id)))
            delete state.boundariesById[Number(id)]
        }

        forgetSnapshots(
          state,
          Object.keys(state.snapshotById)
            .map(Number)
            .filter((id) => !live.has(id)),
        )
      })
      .addCase(fetchTerritorySummaries.rejected, (state, action) => {
        delete state.readStartedAt[action.meta.requestId]
        state.summariesStatus = 'error'
      })
      .addCase(fetchTerritoryBoundaries.pending, (state, action) => {
        state.readStartedAt[action.meta.requestId] = state.writeTick

        // Track in-flight ids so an overlapping viewport change does not request them again.
        state.loadingIds = [
          ...new Set([...state.loadingIds, ...action.meta.arg]),
        ]
      })
      .addCase(fetchTerritoryBoundaries.fulfilled, (state, action) => {
        const startedAt =
          state.readStartedAt[action.meta.requestId] ?? state.writeTick
        delete state.readStartedAt[action.meta.requestId]

        for (const territory of action.payload) {
          // An edit that landed while this request was out is newer than what it is carrying.
          // Writing it back here is what made a redrawn boundary snap to its old shape.
          if ((state.authoritativeAt[territory.id] ?? -1) > startedAt) continue

          if (territory.boundary)
            state.boundariesById[territory.id] = territory.boundary
        }

        // Clear every requested id, not just the ones that came back: an id the server no longer
        // recognises would otherwise stay "loading" forever and never be retried or released.
        const requested = new Set(action.meta.arg)
        state.loadingIds = state.loadingIds.filter((id) => !requested.has(id))
      })
      .addCase(fetchTerritoryBoundaries.rejected, (state, action) => {
        delete state.readStartedAt[action.meta.requestId]
        const requested = new Set(action.meta.arg)
        state.loadingIds = state.loadingIds.filter((id) => !requested.has(id))
      })
      .addCase(updateTerritory.fulfilled, (state, action) => {
        const updated = action.payload
        markWritten(state, updated.id)

        state.summaries = state.summaries.map((t) =>
          t.id === updated.id ? updated : t,
        )

        // The update response carries geometry, so the cache is refreshed rather than evicted.
        // Evicting would leave the polygon missing until the viewport happened to refetch it,
        // and a redrawn boundary would appear to delete itself.
        if (updated.boundary) {
          state.boundariesById[updated.id] = updated.boundary
        }

        // Unconditional, even when only the name changed: the response does not say which
        // fields moved, and re-counting one territory costs a single request.
        forgetSnapshots(state, [updated.id])
      })
      .addCase(deleteTerritory.fulfilled, (state, action) => {
        const id = action.payload
        markWritten(state, id)

        state.summaries = state.summaries.filter((t) => t.id !== id)
        delete state.boundariesById[id]

        // Also drop it from the in-flight set. A delete can land while its geometry request is
        // still out; without this the id would stay "loading" forever and selectIdsToFetch
        // would never consider it again -- harmless for a deleted territory, but it would leak
        // a growing list across a long session.
        state.loadingIds = state.loadingIds.filter((loading) => loading !== id)
        forgetSnapshots(state, [id])
      })
      .addCase(splitTerritory.fulfilled, (state, action) => {
        const { original, created } = action.payload
        markWritten(state, original.id)
        markWritten(state, created.id)

        // BOTH sides changed. Applying only the new one would leave the original drawing its
        // old, larger boundary over the top of the piece just carved out of it.
        state.summaries = upsertSummary(
          state.summaries.map((t) => (t.id === original.id ? original : t)),
          created,
        )

        for (const territory of [original, created]) {
          if (territory.boundary) {
            state.boundariesById[territory.id] = territory.boundary
          }
        }

        forgetSnapshots(state, [original.id, created.id])
      })
      .addCase(mergeTerritories.fulfilled, (state, action) => {
        const merged = action.payload
        const absorbed = new Set(action.meta.arg.sourceIds)

        markWritten(state, merged.id)
        for (const id of absorbed) markWritten(state, id)

        state.summaries = state.summaries
          .filter((t) => !absorbed.has(t.id))
          .map((t) => (t.id === merged.id ? merged : t))

        for (const id of absorbed) delete state.boundariesById[id]
        if (merged.boundary) state.boundariesById[merged.id] = merged.boundary

        state.loadingIds = state.loadingIds.filter((id) => !absorbed.has(id))
        forgetSnapshots(state, [...absorbed, merged.id])
      })
      .addCase(createTerritory.fulfilled, (state, action) => {
        const created = action.payload
        markWritten(state, action.payload.id)

        // Seed both halves so the new territory draws the moment the dialog closes. The create
        // response is the one place the server returns geometry unasked, so no refetch is needed
        // -- and waiting for one would leave the admin staring at the blank space they just drew.
        state.summaries = upsertSummary(state.summaries, created)
        if (created.boundary) {
          state.boundariesById[created.id] = created.boundary
        }
      })
      .addCase(fetchTerritorySnapshot.pending, (state, action) => {
        const { id } = action.meta.arg

        // Stamped for the same reason the summaries and boundaries reads stamp it. A snapshot
        // request can still be out when the admin redraws, splits or merges that territory:
        // the write calls forgetSnapshots, then the old response lands and files counts for a
        // shape that no longer exists, which is exactly what forgetSnapshots is there to stop.
        state.readStartedAt[action.meta.requestId] = state.writeTick

        state.snapshotLoadingIds = [
          ...new Set([...state.snapshotLoadingIds, id]),
        ]
        // Cleared on the way in, not on success: a retry should show a skeleton, not the
        // previous failure sitting under a spinner.
        state.snapshotErrorIds = state.snapshotErrorIds.filter(
          (errored) => errored !== id,
        )
      })
      .addCase(fetchTerritorySnapshot.fulfilled, (state, action) => {
        const { id } = action.meta.arg
        const superseded = snapshotSuperseded(state, action.meta.requestId, id)

        // The loading flag is cleared either way -- this request IS finished, and leaving the
        // id in the set would hold a spinner open forever.
        state.snapshotLoadingIds = state.snapshotLoadingIds.filter(
          (loading) => loading !== id,
        )

        if (superseded) return

        state.snapshotById[id] = action.payload
      })
      .addCase(fetchTerritorySnapshot.rejected, (state, action) => {
        const { id } = action.meta.arg
        const superseded = snapshotSuperseded(state, action.meta.requestId, id)

        state.snapshotLoadingIds = state.snapshotLoadingIds.filter(
          (loading) => loading !== id,
        )

        // A failure that belongs to a shape that no longer exists must not mark the CURRENT
        // one as errored, or the bubble offers a retry link for a request nobody made.
        if (superseded) return

        state.snapshotErrorIds = [...new Set([...state.snapshotErrorIds, id])]
      })
  },
})

export const selectTerritorySummaries = (state: StoreData) =>
  state.territory.summaries
export const selectTerritorySummariesStatus = (state: StoreData) =>
  state.territory.summariesStatus
export const selectTerritoryBoundariesById = (state: StoreData) =>
  state.territory.boundariesById
export const selectTerritoryLoadingIds = (state: StoreData) =>
  state.territory.loadingIds
export const selectTerritorySnapshotsById = (state: StoreData) =>
  state.territory.snapshotById
export const selectTerritorySnapshotLoadingIds = (state: StoreData) =>
  state.territory.snapshotLoadingIds
export const selectTerritorySnapshotErrorIds = (state: StoreData) =>
  state.territory.snapshotErrorIds

export default territorySlice.reducer
